import {
  EliteLandingPageState,
  type EliteConsentTextItem,
  type EliteInsightsCta,
  type EliteMoney,
  type ElitePlanOfferType,
  type ElitePlanTenure,
  type EliteSubscriptionDetails,
  type EliteUserSubscriptionDetails,
  UNITS
} from './misc.types'

/** CTA on elite-prime insight blocks — method/url often omitted when navigationStyle is NONE. */
export type ElitePrimeInsightCta = {
  type: 'CTA'
  ctaType: string
  method?: string
  navigationStyle?: string
  url?: string
  isNavigationDeferred?: boolean
  requestPayload?: string
}

export type ElitePrimePercentage = {
  amount: number
  unit: `${UNITS.PERCENTAGE}`
}

export type ElitePrimePlanTenure = ElitePlanTenure & {
  noOfDays?: number
}

export type ElitePrimeEligiblePlan = {
  basePlanId: string
  effectivePlanId?: string
  effectivePlanFees?: EliteMoney
  effectivePlanFeesInclGst?: EliteMoney
  basePlanFees: EliteMoney
  basePlanFeesInclGst: EliteMoney
  basePlanTenure: ElitePrimePlanTenure
  effectivePlanTenure?: ElitePrimePlanTenure
  specialOffering?: boolean
  priority?: number
  recommended?: boolean
  offerType?: ElitePlanOfferType
  /** Extra tenure days/months for TENURE_BASED offers (same semantics as advisory plans). */
  tenureDiff?: number
  baseSlabPricePercentage?: ElitePrimePercentage
  effectiveSlabPricePercentage?: ElitePrimePercentage
  yearlyPlan?: boolean
}

export type ElitePrimeRegularPlanAlert = {
  cta: EliteInsightsCta | ElitePrimeInsightCta
  extraCommissions: number
}

export type ElitePrimeTaxSummary = {
  cta: EliteInsightsCta | ElitePrimeInsightCta
  summaryType: string
  financialYear: string
  taxLiability: number
}

export type ElitePrimeRegularToDirect = {
  fundsCount: number
  regularToDirectCtaRequest: EliteInsightsCta | ElitePrimeInsightCta
}

/** Elite (non-prime) insight strip returned on the prime summary payload. */
export type ElitePrimeEliteInsights = {
  taxSummaryCta?: EliteInsightsCta | ElitePrimeInsightCta
  rebalanceCtaRequest?: EliteInsightsCta | ElitePrimeInsightCta
  powerSelectCtaRequest?: EliteInsightsCta | ElitePrimeInsightCta
  regularPlanAlert?: ElitePrimeRegularPlanAlert
  taxSummary?: ElitePrimeTaxSummary
  regularToDirect?: ElitePrimeRegularToDirect
  userEligibleForRebalance: boolean
  userElite: boolean
  rebalanceAvailable: boolean
}

export type ElitePrimeAllocationPlanData = {
  fundsToSell?: number
  fundsToBuy?: number
  fundsFrom?: number
  fundsTo?: number
  fundsUnderReview?: number
  planAvailableAt?: number
  /** Days until allocation / lump-sum plan is ready (under-review insight strip). */
  planAvailableInDays?: number
  cta: ElitePrimeInsightCta
}

export type ElitePrimeAllocationSipPlanData = {
  pauseSipCount?: number
  startSipCount?: number
  sipFrom?: number
  sipTo?: number
  planAvailableAt?: number
  cta: ElitePrimeInsightCta
}

export type ElitePrimeIdealPlanData = {
  idealFunds?: number
  idealCategories?: number
  planAvailableAt?: number
  cta: ElitePrimeInsightCta
}

export enum ElitePrimeAllocationCategoryName {
  HIGH_RISK = 'HIGH_RISK',
  MEDIUM_RISK = 'MEDIUM_RISK',
  LOW_RISK = 'LOW_RISK',
  SILVER_AND_GOLD = 'SILVER_AND_GOLD',
  GLOBAL_US = 'GLOBAL_US',
  GLOBAL_NON_CORE = 'GLOBAL_NON_CORE',
  HYBRID = 'HYBRID',
  DEBT = 'DEBT',
  OTHER = 'OTHER',
  INDEX = 'INDEX',
  SECTORAL = 'SECTORAL',
  DEBT_AND_ARBITRAGE = 'DEBT_AND_ARBITRAGE',
  RETIREMENT = 'RETIREMENT',
  CHILDREN = 'CHILDREN',
  NEW_INVESTMENT = 'NEW_INVESTMENT',
  EMERGENCY = 'EMERGENCY'
}

/** Display labels aligned with backend `FundType.label`. */
export function getElitePrimeAllocationCategoryLabel(categoryName: ElitePrimeAllocationCategoryName): string {
  switch (categoryName) {
    case ElitePrimeAllocationCategoryName.HIGH_RISK:
      return 'High Risk'
    case ElitePrimeAllocationCategoryName.MEDIUM_RISK:
      return 'Medium Risk'
    case ElitePrimeAllocationCategoryName.LOW_RISK:
      return 'Low Risk'
    case ElitePrimeAllocationCategoryName.GLOBAL_US:
      return 'Global US'
    case ElitePrimeAllocationCategoryName.HYBRID:
      return 'Hybrid'
    case ElitePrimeAllocationCategoryName.SILVER_AND_GOLD:
      return 'Gold & Silver'
    case ElitePrimeAllocationCategoryName.DEBT:
      return 'Debt'
    case ElitePrimeAllocationCategoryName.OTHER:
      return 'Other'
    case ElitePrimeAllocationCategoryName.SECTORAL:
      return 'Sectoral'
    case ElitePrimeAllocationCategoryName.INDEX:
      return 'Index'
    case ElitePrimeAllocationCategoryName.DEBT_AND_ARBITRAGE:
      return 'Debt and Arbitrage'
    case ElitePrimeAllocationCategoryName.RETIREMENT:
      return 'Retirement'
    case ElitePrimeAllocationCategoryName.CHILDREN:
      return 'Children'
    case ElitePrimeAllocationCategoryName.NEW_INVESTMENT:
      return 'New Investment'
    case ElitePrimeAllocationCategoryName.EMERGENCY:
      return 'Emergency'
    case ElitePrimeAllocationCategoryName.GLOBAL_NON_CORE:
      return 'Global Non Core'
    default: {
      const _exhaustive: never = categoryName
      throw new Error(`Unhandled ElitePrimeAllocationCategoryName: ${String(_exhaustive)}`)
    }
  }
}

export type ElitePrimeAllocationCategory = {
  name: ElitePrimeAllocationCategoryName
  fromPercentage: number
  fromFundCount: number
  /** Target allocation; when present with `toFundCount`, shown instead of the lock. */
  toPercentage?: number
  /** Target fund count; when present with `toPercentage`, shown instead of the lock. */
  toFundCount?: number
}

export enum ElitePrimeRiskProfile {
  VERY_CONSERVATIVE = 'VERY_CONSERVATIVE',
  CONSERVATIVE = 'CONSERVATIVE',
  BALANCED = 'BALANCED',
  AGGRESSIVE = 'AGGRESSIVE',
  VERY_AGGRESSIVE = 'VERY_AGGRESSIVE'
}

export enum ElitePrimeStrategyStatus {
  QUESTIONNAIRE_NOT_INITIATED = 'QUESTIONNAIRE_NOT_INITIATED',
  QUESTIONNAIRE_IN_PROGRESS = 'QUESTIONNAIRE_IN_PROGRESS',
  QUESTIONNAIRE_SUBMITTED = 'QUESTIONNAIRE_SUBMITTED',
  QUESTIONNAIRE_RE_SUBMITTED = 'QUESTIONNAIRE_RE_SUBMITTED',
  PLAN_ROLLED_OUT = 'PLAN_ROLLED_OUT'
}

export type ElitePrimeStrategyData = {
  riskProfile: ElitePrimeRiskProfile
  corePortfolioAmount: number
  otherPortfolioAmount: number
  allocationCategories: ElitePrimeAllocationCategory[]
  status: ElitePrimeStrategyStatus
  planAvailableInDays?: number
  cta?: ElitePrimeInsightCta
}

export type ElitePrimeInsights = {
  allocationStrategyCta: ElitePrimeInsightCta
  globalAdvisoryCta: ElitePrimeInsightCta
  /** Global-funds banner under the advice carousel. Hidden when absent. */
  globalInvestCta?: ElitePrimeInsightCta | null
  /** Lump Sum Investment Plan card CTA — omit/null still shows the card (status only, not tappable). */
  allocationInvestmentStrategyCta?: ElitePrimeInsightCta | null
  allocationWithdrawalStrategyCta: ElitePrimeInsightCta
  advisorSupportCta: ElitePrimeInsightCta
  allocationPlanData: ElitePrimeAllocationPlanData
  allocationSipPlanData: ElitePrimeAllocationSipPlanData
  idealPortfolioData: ElitePrimeIdealPlanData
  idealSipData: ElitePrimeIdealPlanData
  strategyData?: ElitePrimeStrategyData
  userEligibleForRebalance: boolean
  userElite: boolean
  rebalanceAvailable: boolean
}

export type ElitePrimeMarketPricingDetails = {
  marketPrice: EliteMoney
  portfolioPercentage: ElitePrimePercentage
}

export type ElitePrimePortfolioSlab = {
  minPortfolioValue: EliteMoney
  maxPortfolioValue: EliteMoney
  maxSlabPrice: EliteMoney
  slabPercentage: ElitePrimePercentage
}

export type ElitePrimeFundAdditionalCharge = {
  fundName: string
  fundLogo: string
  currentPortfolioValue: EliteMoney
  additionalCharges: EliteMoney
  additionalPercentage: ElitePrimePercentage
}

/** Response body for `GET /v2/portfolio/elite-prime-pricing-details`. */
export type ElitePrimePricingDetailsResponse = {
  currentPortfolioValue: EliteMoney
  portfolioSlabList: ElitePrimePortfolioSlab[]
  fundAdditionalChargesDetails: ElitePrimeFundAdditionalCharge[]
}

/**
 * Response body for `GET /v2/onboarding/advisory/prime-status`
 * (and currently mirrored by the `/v2/elite/summary` mock).
 */
export type ElitePrimeSummaryResponse = {
  userSubscriptionDetails: EliteUserSubscriptionDetails
  eliteLandingPageState: `${EliteLandingPageState}`
  cta: EliteInsightsCta | ElitePrimeInsightCta
  consentText?: string
  throughFamilyAccess?: boolean
  primaryUserName?: string
  secondaryUserName?: string
  consentTextList?: EliteConsentTextItem[]
  eligiblePlans: ElitePrimeEligiblePlan[]
  subscriptionDetails: EliteSubscriptionDetails
  eliteInsights?: ElitePrimeEliteInsights
  elitePrimeInsights?: ElitePrimeInsights
  marketPricingDetails?: ElitePrimeMarketPricingDetails
  isUserElitePrime: boolean
}
